import { Ref, VNode } from 'vue'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Alignment } from '@floating-ui/core'
import type { Options as OffsetOptions } from '@floating-ui/core/src/middleware/offset'
import { Children } from './provision'

export function getRealDomTarget(node: VNode): HTMLElement | undefined {
  if (node.el) {
    // make sure it's a real dom
    if (node.el.getBoundingClientRect) {
      return node.el as HTMLElement
    }

    // otherwise assume it's the first el of a multi-root component(always a text with none dom attrs), try to get it's sibling
    if (node.el.nextElementSibling?.getBoundingClientRect) {
      return node.el.nextElementSibling
    }
  }

  // eslint-disable-next-line no-console
  console.warn('[delight popover] cannot find out the target element for popover, make sure you have passed some meaningful component to the default slot')

  return undefined
}

export function listenWindowMutate(callback: () => void): () => void {
  window.addEventListener('scroll', callback)
  window.addEventListener('resize', callback)

  function stop(): void {
    window.removeEventListener('scroll', callback)
    window.removeEventListener('resize', callback)
  }

  return stop
}

export function listenAncientsMutate(el: HTMLElement, callback: () => void): () => void {
  if (el.parentElement) {
    el.parentElement.addEventListener('scroll', callback)
    el.parentElement.addEventListener('resize', callback)

    listenAncientsMutate(el.parentElement, callback)
  }

  function stop(_el: HTMLElement, _callback: () => void): void {
    if (_el.parentElement) {
      _el.parentElement.removeEventListener('scroll', _callback)
      _el.parentElement.removeEventListener('resize', _callback)

      stop(_el.parentElement, _callback)
    }
  }

  return () => stop(el, callback)
}

export function listenWindowEvent(
  args: {
    popover: HTMLElement
    target: HTMLElement
    children: Ref<Children>
  },
  callback: {
    onClickPopover: (e: MouseEvent) => void
    onClickTarget: (e: MouseEvent) => void
    onClickOutside: (e: MouseEvent) => void
    onMousedownPopover: (e: MouseEvent) => void
    onMousedownTarget: (e: MouseEvent) => void
    onMousedownOutside: (e: MouseEvent) => void
    onMouseupPopover: (e: MouseEvent) => void
    onMouseupTarget: (e: MouseEvent) => void
    onMouseupOutside: (e: MouseEvent) => void
  },
): () => void {
  function recursivelyGetElements(children: Children): HTMLElement[] {
    let elements: HTMLElement[] = []

    children
      .forEach(
        child => {
          elements = [
            ...elements,
            child.popover.el,
            ...recursivelyGetElements(child.popover.children),
          ]
        },
      )

    return elements
  }

  function clickHandler(event: MouseEvent) {
    const onPopover = [args.popover, ...recursivelyGetElements(args.children.value)].some(el => event.composedPath().includes(el))
    const onTarget = event.composedPath().includes(args.target)

    if (onPopover) {
      callback.onClickPopover(event)
    } else if (onTarget) {
      callback.onClickTarget(event)
    } else {
      callback.onClickOutside(event)
    }
  }

  function mousedownHandler(event: MouseEvent) {
    const onPopover = [args.popover, ...recursivelyGetElements(args.children.value)].some(el => event.composedPath().includes(el))
    const onTarget = event.composedPath().includes(args.target)

    if (onPopover) {
      callback.onMousedownPopover(event)
    } else if (onTarget) {
      callback.onMousedownTarget(event)
    } else {
      callback.onMousedownOutside(event)
    }
  }

  function mouseupHandler(event: MouseEvent) {
    const onPopover = [args.popover, ...recursivelyGetElements(args.children.value)].some(el => event.composedPath().includes(el))
    const onTarget = event.composedPath().includes(args.target)

    if (onPopover) {
      callback.onMouseupPopover(event)
    } else if (onTarget) {
      callback.onMouseupTarget(event)
    } else {
      callback.onMouseupOutside(event)
    }
  }

  document.addEventListener('click', clickHandler)
  document.addEventListener('mousedown', mousedownHandler)
  document.addEventListener('mouseup', mouseupHandler)

  return () => {
    document.removeEventListener('click', clickHandler)
    document.removeEventListener('mousedown', mousedownHandler)
    document.removeEventListener('mouseup', mouseupHandler)
  }
}

export function offsetHelper(mainAxis: number, startAlignedCrossAxis: number): OffsetOptions {
  return ({ placement }) => {
    const [, alignment] = placement.split('-')

    switch (alignment as Alignment) {
      case 'start':
        return {
          mainAxis,
          crossAxis: startAlignedCrossAxis,
        }
      case 'end':
        return {
          mainAxis,
          crossAxis: -startAlignedCrossAxis,
        }
      default:
        return {
          mainAxis,
        }
    }
  }
}
